import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';

import { LoadingButton } from '@mui/lab';
import { Autocomplete, Avatar, Box, Card, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { Draggable, Droppable } from '@hello-pangea/dnd';
import { TreeNode } from 'react-organizational-chart';

import { useBoolean } from 'src/hooks/use-boolean';
import { useTranslate } from 'src/locales';
import { useUpdateContactMutation } from 'src/services/contacts/contacts.service';
import type { Contact } from 'src/services/contacts/contacts.types';
import { ConfirmDialog } from 'src/components/custom-dialog';
import Iconify from 'src/components/iconify';

import { buildOrgForest, collectDescendantIds, findNode, type OrgNode } from './build-org-tree';
import { useOrgChartPreview } from './org-chart-preview';

const contactLabel = (c: Contact) =>
  `${c.first_name} ${c.last_name ?? ''}${c.position_title ? ` — ${c.position_title}` : ''}`.trim();

type OrgNodeCardProps = { contact: Contact; companyContacts: Contact[]; companyProfileId: string; canManage: boolean };
type ChangeManagerActionProps = { contact: Contact; companyContacts: Contact[]; companyProfileId: string };

function ChangeManagerAction({ contact, companyContacts, companyProfileId }: ChangeManagerActionProps) {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const dialog = useBoolean();
  const [updateContact, { isLoading }] = useUpdateContactMutation();

  const forbiddenIds = useMemo(() => {
    const forest = buildOrgForest(companyContacts);
    const selfNode = findNode(forest, contact.id);
    const ids = selfNode ? collectDescendantIds(selfNode) : new Set<string>();
    ids.add(contact.id);
    return ids;
  }, [companyContacts, contact.id]);

  const options = useMemo(() => companyContacts.filter((c) => !forbiddenIds.has(c.id)), [companyContacts, forbiddenIds]);
  const currentManager = useMemo(
    () => companyContacts.find((c) => c.id === contact.reports_to_contact_id) ?? null,
    [companyContacts, contact.reports_to_contact_id]
  );
  const [selected, setSelected] = useState<Contact | null>(currentManager);

  const onOpen = () => { setSelected(currentManager); dialog.onTrue(); };

  const save = async (managerId: string | null) => {
    try {
      await updateContact({ contact_id: contact.id, reports_to_contact_id: managerId, company_profile_id: companyProfileId }).unwrap();
      enqueueSnackbar(t('companies.org_chart.change_manager_success'), { variant: 'success' });
      dialog.onFalse();
    } catch (error) {
      const message = (error as { data?: { message?: string } })?.data?.message ?? t('companies.org_chart.change_manager_error');
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  const hasManager = !!contact.reports_to_contact_id;

  return (
    <>
      <Tooltip title={t('companies.org_chart.change_manager')}>
        <IconButton size="small" onClick={onOpen}><Iconify icon="solar:pen-bold" width={16} /></IconButton>
      </Tooltip>
      <ConfirmDialog
        open={dialog.value}
        onClose={dialog.onFalse}
        title={t('companies.org_chart.change_manager')}
        content={
          <Box sx={{ pt: 2, minWidth: 320 }}>
            <Autocomplete
              fullWidth options={options} value={selected} getOptionLabel={contactLabel}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              onChange={(_e, value) => setSelected(value)}
              renderInput={(params) => (
                <TextField {...params} label={t('companies.org_chart.reports_to_label')} placeholder={t('companies.org_chart.reports_to_placeholder')} />
              )}
            />
          </Box>
        }
        action={
          <>
            {hasManager && (
              <LoadingButton color="error" loading={isLoading} onClick={() => save(null)} sx={{ mr: 'auto' }}>
                {t('companies.org_chart.unassign_action')}
              </LoadingButton>
            )}
            <LoadingButton variant="contained" loading={isLoading} onClick={() => save(selected?.id ?? null)}>
              {t('common.save')}
            </LoadingButton>
          </>
        }
      />
    </>
  );
}

export function OrgNodeCard({ contact, companyContacts, companyProfileId, canManage }: OrgNodeCardProps) {
  const { t } = useTranslate();
  const openPreview = useOrgChartPreview();
  const openProfile = (event: React.MouseEvent) => {
    event.stopPropagation(); // don't let the click start a drag
    // Open the contact in the quick side-panel over the chart. Backed by local state (not the URL
    // record-panel singleton) so it layers cleanly — even when the chart is itself inside a
    // company overlay — instead of evicting whatever panel owns ?preview.
    openPreview(contact.id);
  };
  return (
    <Card variant="outlined" sx={{ p: 1.5, width: 220, minHeight: 64, display: 'inline-flex', alignItems: 'center', textAlign: 'left' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
        <Avatar sx={{ width: 28, height: 28, fontSize: 14, flexShrink: 0 }}>{contact.first_name?.[0]?.toUpperCase()}</Avatar>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography variant="subtitle2" noWrap sx={{ textTransform: 'capitalize' }}>{contact.first_name} {contact.last_name}</Typography>
          {/* Always render the title line (placeholder when empty) so boxes stay uniform. */}
          <Typography variant="caption" color="text.secondary" noWrap component="div" sx={{ minHeight: 18 }}>
            {contact.position_title || ' '}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.25} alignItems="center" sx={{ flexShrink: 0 }}>
          <Tooltip title={t('companies.org_chart.view_profile')}>
            <IconButton size="small" onClick={openProfile}><Iconify icon="solar:eye-bold" width={16} /></IconButton>
          </Tooltip>
          {canManage && (
            <ChangeManagerAction contact={contact} companyContacts={companyContacts} companyProfileId={companyProfileId} />
          )}
        </Stack>
      </Stack>
    </Card>
  );
}

/**
 * Every card is both draggable (re-assign its manager) and a drop target (drop another card
 * onto it to make that person report to this one). droppableId === draggableId === contact.id;
 * the container's onDragEnd interprets the pairing. Placeholder is hidden so hovering a drop
 * target doesn't reflow the 2D chart. When `canManage` is false the card renders plain (no
 * drag/drop wrappers) for read-only viewers (e.g. client tenants).
 */
export function DraggableOrgCard({ contact, companyContacts, companyProfileId, canManage }: OrgNodeCardProps) {
  if (!canManage) {
    return <OrgNodeCard contact={contact} companyContacts={companyContacts} companyProfileId={companyProfileId} canManage={canManage} />;
  }
  return (
    <Droppable droppableId={contact.id}>
      {(dropProvided, dropSnapshot) => (
        <Box
          ref={dropProvided.innerRef} {...dropProvided.droppableProps}
          sx={(theme) => ({
            borderRadius: 1, p: 0.5,
            outline: dropSnapshot.isDraggingOver ? `2px dashed ${theme.palette.primary.main}` : '2px dashed transparent',
            transition: 'outline-color .15s',
          })}
        >
          <Draggable draggableId={contact.id} index={0}>
            {(dragProvided, dragSnapshot) => (
              <Box
                ref={dragProvided.innerRef} {...dragProvided.draggableProps} {...dragProvided.dragHandleProps}
                sx={{ opacity: dragSnapshot.isDragging ? 0.9 : 1, cursor: 'grab' }}
              >
                <OrgNodeCard contact={contact} companyContacts={companyContacts} companyProfileId={companyProfileId} canManage={canManage} />
              </Box>
            )}
          </Draggable>
          <Box sx={{ display: 'none' }}>{dropProvided.placeholder}</Box>
        </Box>
      )}
    </Droppable>
  );
}

type OrgChartBranchProps = { node: OrgNode; companyContacts: Contact[]; companyProfileId: string; canManage: boolean };

export function OrgChartBranch({ node, companyContacts, companyProfileId, canManage }: OrgChartBranchProps) {
  return (
    <TreeNode label={<DraggableOrgCard contact={node.contact} companyContacts={companyContacts} companyProfileId={companyProfileId} canManage={canManage} />}>
      {node.children.map((child) => (
        <OrgChartBranch key={child.contact.id} node={child} companyContacts={companyContacts} companyProfileId={companyProfileId} canManage={canManage} />
      ))}
    </TreeNode>
  );
}
